import type { Dictionary } from '@/types/utils'

// Replaces params inside handlebars {{}} with the provided values
// Spaces inside handlebars are ignored
// Vars not found in the dictionary are replaced with an empty string
// parseStringTemplate(
//    'Hello, {{ name   }}! Are you {{age}} years old?',
//    { name: 'John', age: 25 }
// ) // => 'Hello, John! Are you 25 years old?'
export function parseStringTemplate(
    template: string,
    values: Dictionary<string | number>
): string {
    const parts = template.split(/\{\{(?!\d)[\wæøåÆØÅ\s]*\}\}/)
    const parameters =
        template.match(/[^{}]+(?=})/g)?.map((s) => s.trim()) || []
    const args = parameters.map((parameter) => values[parameter] ?? '')

    const ret = String.raw({ raw: parts }, ...args).trim()

    return ret.replaceAll(/\s+/g, ' ')
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    describe('parseStringTemplate', () => {
        test('Test with leading placeholders', () => {
            expect(
                parseStringTemplate(
                    '{{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}} engagement rings - 2024 Collection',
                    {
                        design_style: 'design_style',
                        numeric_metal: 'numeric_metal',
                        gem_shape: 'gem_shape',
                        gem_type: 'gem_type',
                        ring_style: 'ring_style',
                    }
                )
            ).toBe(
                'design_style numeric_metal gem_shape gem_type ring_style engagement rings - 2024 Collection'
            )
        })

        test('Test with leading placeholders and empty start', () => {
            expect(
                parseStringTemplate(
                    '{{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}} engagement rings - 2024 Collection',
                    {
                        design_style: '',
                        numeric_metal: '',
                        gem_shape: 'gem_shape',
                        gem_type: 'gem_type',
                        ring_style: 'ring_style',
                    }
                )
            ).toBe(
                'gem_shape gem_type ring_style engagement rings - 2024 Collection'
            )
        })

        test('Test with empty leading placeholders and empty mid', () => {
            expect(
                parseStringTemplate(
                    '{{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}} engagement rings - 2024 Collection',
                    {
                        design_style: 'design_style',
                        numeric_metal: 'numeric_metal',
                        gem_shape: '',
                        gem_type: '',
                        ring_style: 'ring_style',
                    }
                )
            ).toBe(
                'design_style numeric_metal ring_style engagement rings - 2024 Collection'
            )
        })

        test('Test with empty leading placeholders and empty end', () => {
            expect(
                parseStringTemplate(
                    '{{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}} engagement rings - 2024 Collection',
                    {
                        design_style: 'design_style',
                        numeric_metal: 'numeric_metal',
                        gem_shape: 'gem_shape',
                        gem_type: '',
                        ring_style: '',
                    }
                )
            ).toBe(
                'design_style numeric_metal gem_shape engagement rings - 2024 Collection'
            )
        })

        test('Test with end placeholders', () => {
            expect(
                parseStringTemplate(
                    'engagement rings - 2024 Collection {{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}}',
                    {
                        design_style: 'design_style',
                        numeric_metal: 'numeric_metal',
                        gem_shape: 'gem_shape',
                        gem_type: 'gem_type',
                        ring_style: 'ring_style',
                    }
                )
            ).toBe(
                'engagement rings - 2024 Collection design_style numeric_metal gem_shape gem_type ring_style'
            )
        })

        test('Test with end placeholders and empty start', () => {
            expect(
                parseStringTemplate(
                    'engagement rings - 2024 Collection {{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}}',
                    {
                        design_style: '',
                        numeric_metal: '',
                        gem_shape: 'gem_shape',
                        gem_type: 'gem_type',
                        ring_style: 'ring_style',
                    }
                )
            ).toBe(
                'engagement rings - 2024 Collection gem_shape gem_type ring_style'
            )
        })

        test('Test with end placeholders and empty mid', () => {
            expect(
                parseStringTemplate(
                    'engagement rings - 2024 Collection {{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}}',
                    {
                        design_style: 'design_style',
                        numeric_metal: 'numeric_metal',
                        gem_shape: '',
                        gem_type: '',
                        ring_style: 'ring_style',
                    }
                )
            ).toBe(
                'engagement rings - 2024 Collection design_style numeric_metal ring_style'
            )
        })

        test('Test with end placeholders and empty end', () => {
            expect(
                parseStringTemplate(
                    'engagement rings - 2024 Collection {{design_style}} {{numeric_metal}} {{gem_shape}} {{gem_type}} {{ring_style}}',
                    {
                        design_style: 'design_style',
                        numeric_metal: 'numeric_metal',
                        gem_shape: 'gem_shape',
                        gem_type: '',
                        ring_style: '',
                    }
                )
            ).toBe(
                'engagement rings - 2024 Collection design_style numeric_metal gem_shape'
            )
        })

        test('Test with random placeholders and random empty ones', () => {
            expect(
                parseStringTemplate(
                    '{{some_lead_placeholder}} Test {{with_empty}} {{next_to_non_empty}} And {{some_ending_value}}{{next_to_another}} {{and_empty}}',
                    {
                        some_lead_placeholder: 'some_lead_placeholder',
                        with_empty: '',
                        next_to_non_empty: 'next_to_non_empty',
                        some_ending_value: 'some_ending_value',
                        next_to_another: 'next_to_another',
                        and_empty: '',
                    }
                )
            ).toBe(
                'some_lead_placeholder Test next_to_non_empty And some_ending_valuenext_to_another'
            )
        })
    })
}
